import type { Point2D, GeoCoord } from './mapGeometry';
import { projectToLocalMeters } from './mapGeometry';
import type { TrackPoint, SplineSample } from '@/lib/spline';
import { findClosestParametricT, haversineDistanceM } from '@/lib/spline';

export interface TrackRibbonDrawOptions {
  fillColor?: string | null;
  borderColor?: string;
  borderWidth?: number;
  innerBorderColor?: string;
  outerBorderColor?: string;
}

/**
 * Draws the asphalt track ribbon between inner and outer boundary polylines.
 */
export function drawTrackRibbon(
  ctx: CanvasRenderingContext2D,
  inner: TrackPoint[],
  outer: TrackPoint[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  options: TrackRibbonDrawOptions = {}
) {
  if (inner.length < 3 || outer.length < 3) return;

  const {
    fillColor = 'rgba(22, 24, 32, 0.92)',
    borderColor = 'rgba(255, 255, 255, 0.25)',
    borderWidth = 1.2,
    innerBorderColor,
    outerBorderColor,
  } = options;

  const innerCanvas = inner.map(p => toCanvas(projectToLocalMeters(p, anchor)));
  const outerCanvas = outer.map(p => toCanvas(projectToLocalMeters(p, anchor)));

  // 1. Asphalt polygon
  if (fillColor && fillColor !== 'transparent' && fillColor !== 'none') {
    ctx.beginPath();
    innerCanvas.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    for (let i = outerCanvas.length - 1; i >= 0; i--) {
      ctx.lineTo(outerCanvas[i].x, outerCanvas[i].y);
    }
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();
  }

  // 2. Borders
  // Inner border
  ctx.beginPath();
  innerCanvas.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
  ctx.strokeStyle = innerBorderColor || borderColor;
  ctx.lineWidth = borderWidth;
  ctx.stroke();

  // Outer border
  ctx.beginPath();
  outerCanvas.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
  ctx.strokeStyle = outerBorderColor || borderColor;
  ctx.lineWidth = borderWidth;
  ctx.stroke();
}

/**
 * Draws the track spline centerline.
 */
export function drawCenterline(
  ctx: CanvasRenderingContext2D,
  samples: SplineSample[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  color = 'rgba(124, 92, 252, 0.4)',
  lineWidth = 2
) {
  if (samples.length < 2) return;

  ctx.beginPath();
  samples.forEach((s, i) => {
    const cp = toCanvas(projectToLocalMeters(s.point, anchor));
    if (i === 0) ctx.moveTo(cp.x, cp.y);
    else ctx.lineTo(cp.x, cp.y);
  });
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/**
 * Draws a finish line across track width at a given track location.
 */
export function drawFinishLine(
  ctx: CanvasRenderingContext2D,
  finishPt: { point: TrackPoint; normal: { x: number; y: number } },
  trackWidthM: number,
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  lineWidth = 3
) {
  const halfWidth = trackWidthM / 2;
  const latRad = (finishPt.point.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);

  const dLatInner = ((finishPt.normal.y * halfWidth) / 6371000.0) * (180 / Math.PI);
  const dLonInner = ((finishPt.normal.x * halfWidth) / (6371000.0 * cosLat)) * (180 / Math.PI);

  const innerGeo = { lat: finishPt.point.lat + dLatInner, lng: finishPt.point.lng + dLonInner };
  const outerGeo = { lat: finishPt.point.lat - dLatInner, lng: finishPt.point.lng - dLonInner };

  const cInner = toCanvas(projectToLocalMeters(innerGeo, anchor));
  const cOuter = toCanvas(projectToLocalMeters(outerGeo, anchor));

  // High-contrast finish line
  ctx.beginPath();
  ctx.moveTo(cInner.x, cInner.y);
  ctx.lineTo(cOuter.x, cOuter.y);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

export interface CornerHighlightItem {
  index: number;
  label?: string;
  orderIndex?: number;
  apexPoint?: { lat: number; lng: number };
  entryPoint?: { lat: number; lng: number };
  exitPoint?: { lat: number; lng: number };
}

export interface CornerCanvasHitItem {
  index: number;
  apex: Point2D;
  centerlinePts?: Point2D[];
}

export interface DrawCornerHighlightsOptions {
  selectedIndex?: number;
  hoveredIndex?: number;
  showBadges?: boolean;
}

/**
 * Calculates the forward spline sample indices covering a corner from entry to exit through apex.
 */
export function getCornerSplineIndices(
  entryPt: { lat: number; lng: number } | undefined,
  apexPt: { lat: number; lng: number },
  exitPt: { lat: number; lng: number } | undefined,
  splineSamples: SplineSample[]
): number[] {
  const N = splineSamples.length;
  if (N === 0) return [];
  if (N < 3) return [0];

  const apexInfo = findClosestParametricT(apexPt, splineSamples);
  const a = apexInfo.index;

  const defaultStep = Math.max(3, Math.round(N * 0.03));
  const e = entryPt ? findClosestParametricT(entryPt, splineSamples).index : (a - defaultStep + N) % N;
  const x = exitPt ? findClosestParametricT(exitPt, splineSamples).index : (a + defaultStep) % N;

  const fwdSteps = (x - e + N) % N;
  const apexSteps = (a - e + N) % N;

  const indices: number[] = [];
  if (apexSteps <= fwdSteps && fwdSteps < N * 0.75) {
    let curr = e;
    for (let step = 0; step <= fwdSteps; step++) {
      indices.push(curr);
      if (curr === x) break;
      curr = (curr + 1) % N;
    }
  } else {
    const revSteps = (e - x + N) % N;
    let curr = x;
    for (let step = 0; step <= revSteps; step++) {
      indices.push(curr);
      if (curr === e) break;
      curr = (curr + 1) % N;
    }
  }

  return indices;
}

/**
 * Calculates GPS sample indices covering a corner from entry to exit through apex (for fallback trace).
 */
export function getCornerGpsIndices(
  entryPt: { lat: number; lng: number } | undefined,
  apexPt: { lat: number; lng: number },
  exitPt: { lat: number; lng: number } | undefined,
  gpsPoints: Array<{ lat: number; lng: number }>
): number[] {
  const N = gpsPoints.length;
  if (N === 0) return [];
  if (N < 3) return [0];

  let bestApexIdx = 0;
  let bestApexDist = Infinity;
  for (let i = 0; i < N; i++) {
    const d = haversineDistanceM(apexPt, gpsPoints[i]);
    if (d < bestApexDist) {
      bestApexDist = d;
      bestApexIdx = i;
    }
  }

  const defaultOffset = Math.max(2, Math.round(N * 0.03));
  let bestEntryIdx = Math.max(0, bestApexIdx - defaultOffset);
  if (entryPt) {
    let bestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const d = haversineDistanceM(entryPt, gpsPoints[i]);
      if (d < bestDist) {
        bestDist = d;
        bestEntryIdx = i;
      }
    }
  }

  let bestExitIdx = Math.min(N - 1, bestApexIdx + defaultOffset);
  if (exitPt) {
    let bestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const d = haversineDistanceM(exitPt, gpsPoints[i]);
      if (d < bestDist) {
        bestDist = d;
        bestExitIdx = i;
      }
    }
  }

  const start = Math.min(bestEntryIdx, bestExitIdx);
  const end = Math.max(bestEntryIdx, bestExitIdx);
  const indices: number[] = [];
  for (let i = start; i <= end; i++) {
    indices.push(i);
  }
  return indices;
}

/**
 * Draws corner highlights along the track shape (ribbon fill, yellow borders, end-cap ticks, and centerline).
 * Also renders turn badges at the apex for corner identification.
 */
export function drawCornerTrackHighlights(
  ctx: CanvasRenderingContext2D,
  corners: CornerHighlightItem[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  options: DrawCornerHighlightsOptions & {
    splineSamples?: SplineSample[];
    boundaries?: { inner: TrackPoint[]; outer: TrackPoint[] };
    gpsPoints?: Array<{ lat: number; lng: number }>;
  }
): CornerCanvasHitItem[] {
  const {
    selectedIndex = -1,
    hoveredIndex = -1,
    showBadges = true,
    splineSamples,
    boundaries,
    gpsPoints = [],
  } = options;

  const hitItems: CornerCanvasHitItem[] = [];
  const hasRibbon = !!(boundaries && boundaries.inner.length > 0 && boundaries.outer.length > 0 && splineSamples && splineSamples.length > 0);

  // Sort corners so that unselected are rendered first, then hovered, then selected on top
  const sortedCorners = [...corners].sort((a, b) => {
    const scoreA = a.index === selectedIndex ? 2 : a.index === hoveredIndex ? 1 : 0;
    const scoreB = b.index === selectedIndex ? 2 : b.index === hoveredIndex ? 1 : 0;
    return scoreA - scoreB;
  });

  // 1. Draw track shape highlights (ribbon & centerline) for each corner
  for (const c of sortedCorners) {
    if (!c.apexPoint) continue;
    const isSelected = c.index === selectedIndex;
    const isHovered = c.index === hoveredIndex;

    if (hasRibbon) {
      const indices = getCornerSplineIndices(c.entryPoint, c.apexPoint, c.exitPoint, splineSamples);
      if (indices.length < 2) continue;

      const innerCanvas = indices.map(i => toCanvas(projectToLocalMeters(boundaries.inner[i], anchor)));
      const outerCanvas = indices.map(i => toCanvas(projectToLocalMeters(boundaries.outer[i], anchor)));
      const centerCanvas = indices.map(i => toCanvas(projectToLocalMeters(splineSamples[i].point, anchor)));

      // 1.1 Ribbon polygon fill
      ctx.beginPath();
      innerCanvas.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      for (let idx = outerCanvas.length - 1; idx >= 0; idx--) {
        ctx.lineTo(outerCanvas[idx].x, outerCanvas[idx].y);
      }
      ctx.closePath();
      ctx.fillStyle = isSelected
        ? 'rgba(250, 204, 21, 0.45)'
        : isHovered
        ? 'rgba(250, 204, 21, 0.32)'
        : 'rgba(250, 204, 21, 0.16)';
      ctx.fill();

      // 1.2 Ribbon boundaries (yellow borders)
      ctx.beginPath();
      innerCanvas.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = isSelected ? '#facc15' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.45)';
      ctx.lineWidth = isSelected ? 2.5 : isHovered ? 2 : 1.2;
      ctx.stroke();

      ctx.beginPath();
      outerCanvas.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = isSelected ? '#facc15' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.45)';
      ctx.lineWidth = isSelected ? 2.5 : isHovered ? 2 : 1.2;
      ctx.stroke();

      // 1.3 Entrance & Exit end-cap demarcation lines
      if (innerCanvas.length > 0 && outerCanvas.length > 0) {
        ctx.beginPath();
        ctx.moveTo(innerCanvas[0].x, innerCanvas[0].y);
        ctx.lineTo(outerCanvas[0].x, outerCanvas[0].y);
        ctx.strokeStyle = isSelected ? '#facc15' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.45)';
        ctx.lineWidth = isSelected ? 2 : 1.2;
        ctx.stroke();

        const last = innerCanvas.length - 1;
        ctx.beginPath();
        ctx.moveTo(innerCanvas[last].x, innerCanvas[last].y);
        ctx.lineTo(outerCanvas[last].x, outerCanvas[last].y);
        ctx.strokeStyle = isSelected ? '#facc15' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.45)';
        ctx.lineWidth = isSelected ? 2 : 1.2;
        ctx.stroke();
      }

      // 1.4 Centerline highlight
      ctx.save();
      if (isSelected) {
        ctx.shadowColor = 'rgba(250, 204, 21, 0.7)';
        ctx.shadowBlur = 6;
      }
      ctx.beginPath();
      centerCanvas.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = isSelected ? '#fef08a' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.55)';
      ctx.lineWidth = isSelected ? 3.5 : isHovered ? 2.5 : 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();
    } else if (gpsPoints.length > 1) {
      // Fallback GPS line
      const indices = getCornerGpsIndices(c.entryPoint, c.apexPoint, c.exitPoint, gpsPoints);
      if (indices.length < 2) continue;

      const gpsCanvasPts = indices.map(i => toCanvas(projectToLocalMeters(gpsPoints[i], anchor)));
      ctx.save();
      if (isSelected) {
        ctx.shadowColor = 'rgba(250, 204, 21, 0.75)';
        ctx.shadowBlur = 8;
      }
      ctx.beginPath();
      gpsCanvasPts.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = isSelected ? '#facc15' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.5)';
      ctx.lineWidth = isSelected ? 5.5 : isHovered ? 4 : 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();
    }
  }

  // 2. Draw Apex badges and collect hit items
  for (const c of corners) {
    if (!c.apexPoint) continue;
    const cApex = toCanvas(projectToLocalMeters(c.apexPoint, anchor));
    const isSelected = c.index === selectedIndex;
    const isHovered = c.index === hoveredIndex;
    const badgeRadius = isSelected ? 15 : isHovered ? 13 : 12;

    hitItems.push({
      index: c.index,
      apex: cApex,
    });

    if (showBadges) {
      // Glow effect
      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(cApex.x, cApex.y, badgeRadius + (isSelected ? 6 : 4), 0, Math.PI * 2);
        ctx.fillStyle = isSelected ? 'rgba(250, 204, 21, 0.4)' : 'rgba(250, 204, 21, 0.2)';
        ctx.fill();
      }

      // Badge circle
      ctx.beginPath();
      ctx.arc(cApex.x, cApex.y, badgeRadius, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#facc15' : isHovered ? '#334155' : '#1e293b';
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#ffffff' : isHovered ? '#facc15' : 'rgba(250, 204, 21, 0.5)';
      ctx.lineWidth = isSelected ? 2 : 1.2;
      ctx.stroke();

      // Corner label
      const label = c.label || String(c.orderIndex || c.index + 1);
      ctx.fillStyle = isSelected ? '#0f172a' : '#ffffff';
      ctx.font = `bold ${isSelected ? 11 : 9}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cApex.x, cApex.y);
    }
  }

  return hitItems;
}

/**
 * Samples the closed spline at a target distance along the loop, returning interpolated
 * point, unit normal vector, and local track width in meters.
 */
export function sampleTrackRibbonAtDistance(
  targetDistM: number,
  samples: SplineSample[],
  totalLengthM: number
): { point: TrackPoint; normal: { x: number; y: number }; widthM: number } {
  const n = samples.length;
  if (n === 0) return { point: { lat: 0, lng: 0 }, normal: { x: 0, y: 1 }, widthM: 8.0 };
  const totalLen = totalLengthM > 0 ? totalLengthM : 1.0;
  const d = ((targetDistM % totalLen) + totalLen) % totalLen;

  let low = 0;
  let high = n - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (samples[mid].distanceM <= d) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const i0 = Math.max(0, Math.min(n - 1, high));
  const i1 = (i0 + 1) % n;
  const s0 = samples[i0];
  const s1 = samples[i1];

  const d0 = s0.distanceM;
  const d1 = i1 === 0 ? totalLen : s1.distanceM;
  const span = d1 - d0;
  const u = span > 1e-6 ? Math.max(0, Math.min(1, (d - d0) / span)) : 0;

  const lat = s0.point.lat + u * (s1.point.lat - s0.point.lat);
  const lng = s0.point.lng + u * (s1.point.lng - s0.point.lng);
  const nx = s0.normal.x + u * (s1.normal.x - s0.normal.x);
  const ny = s0.normal.y + u * (s1.normal.y - s0.normal.y);
  const nLen = Math.sqrt(nx * nx + ny * ny) || 1;
  const widthM = s0.widthM + u * (s1.widthM - s0.widthM);

  return {
    point: { lat, lng },
    normal: { x: nx / nLen, y: ny / nLen },
    widthM,
  };
}

/**
 * Offsets a sampled spline point laterally along its local unit normal by `offsetM` meters.
 */
export function offsetRibbonPoint(
  sample: { point: TrackPoint; normal: { x: number; y: number } },
  offsetM: number
): GeoCoord {
  const latRad = (sample.point.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad) || 1;
  const dLat = ((sample.normal.y * offsetM) / 6371000.0) * (180 / Math.PI);
  const dLon = ((sample.normal.x * offsetM) / (6371000.0 * cosLat)) * (180 / Math.PI);
  return {
    lat: sample.point.lat + dLat,
    lng: sample.point.lng + dLon,
  };
}


